/**
 * Generates meaningful short titles for posts by finding the minimum number of characters
 * that make each title unique and meaningful
 */

// Module-level cache for tracking used titles
const usedTitlesCache = new Set()

/**
 * Cleans text by removing or handling punctuation intelligently
 * @param {string} text - The text to clean
 * @returns {string} - Cleaned text
 */
function cleanText (text) {
  if (!text) return ''

  // Handle brackets - if there's an opening bracket followed by content and a closing bracket,
  // try to use the content inside brackets instead of the opening bracket
  const bracketMatch = text.match(/^[^\w]*[([{]([^)\]}]+)[)\]}]/)
  if (bracketMatch && bracketMatch[1]) {
    return bracketMatch[1].trim()
  }

  // Remove leading punctuation and spaces, but keep if title really starts with them
  // (i.e., if after removing them we have less than 2 characters)
  let cleaned = text.trim()

  // Try removing leading non-word characters
  const withoutLeadingPunct = cleaned.replace(/^[^\w]+/, '')

  // If we still have meaningful content after removing punctuation, use it
  if (withoutLeadingPunct.length >= 2) {
    cleaned = withoutLeadingPunct
  }

  return cleaned
}

/**
 * Removes common articles and handles spaces
 * @param {string} text - The text to process
 * @returns {string} - Text without articles and with collapsed spaces
 */
function removeArticlesAndSpaces (text) {
  if (!text) return ''

  // Collapse multiple spaces into single spaces and trim first
  const processed = text.replace(/\s+/g, ' ').trim()

  // Remove common articles at the beginning (case insensitive) only if followed by space
  const withoutArticles = processed.replace(/^(the|a|an)\s+/i, '')

  // If removing articles left us with less than 2 characters, or if the original was just an article, keep the original
  if (withoutArticles.length < 2 || processed.match(/^(the|a|an)$/i)) {
    return processed
  }

  return withoutArticles
}

/**
 * Extracts meaningful characters from text, prioritizing word characters
 * @param {string} text - The text to extract from
 * @param {number} length - The desired length
 * @returns {string} - Extracted meaningful characters
 */
function extractMeaningfulChars (text, requestedLength) {
  if (!text) return ''

  // For single character originals, keep them as single character
  if (text.trim().length === 1) {
    return text.trim()
  }

  // Clean the text and remove articles
  const cleaned = removeArticlesAndSpaces(cleanText(text))
  if (cleaned.length === 0) return text.substring(0, Math.min(2, text.length))

  // Split into words
  const words = cleaned.split(/[\s\-_.]+/).filter(word => word.length > 0)

  if (words.length === 0) return cleaned.substring(0, Math.min(2, cleaned.length))

  // Strategy based on your requirements:
  // 1. Try 2 characters from first word
  if (requestedLength === 2) {
    return words[0].substring(0, 2)
  }

  // 2. Try 3 characters from first word
  if (requestedLength === 3) {
    return words[0].substring(0, 3)
  }

  // 3. Try 3 characters + first letter of second word (if exists)
  if (requestedLength === 4 && words.length > 1) {
    return words[0].substring(0, 3) + words[1][0].toUpperCase()
  }

  // Fallback: truncate first word to requested length (max 4)
  return words[0].substring(0, Math.min(requestedLength, 4))
}

/**
 * Clears the module-level cache of used titles
 */
export function clearShortTitlesCache () {
  usedTitlesCache.clear()
}

/**
 * Registers an existing short title in the cache to prevent duplicates
 * @param {string} shortTitle - The short title to register
 */
export function registerShortTitle (shortTitle) {
  if (shortTitle) {
    usedTitlesCache.add(shortTitle)
  }
}

/**
 * Generates a short title for a single title string, ensuring uniqueness
 * @param {string} title - The original title
 * @returns {string} Short title
 */
export function generateShortTitle (title) {
  if (typeof title !== 'string') title = '?'
  title = title.trim()
  if (!title) title = '?'
  let shortTitle = ''

  // Handle single-character original titles
  if (title.trim().length === 1) {
    shortTitle = title.trim()
    if (usedTitlesCache.has(shortTitle)) {
      let counter = 1
      let numberedTitle = shortTitle + counter
      while (usedTitlesCache.has(numberedTitle)) {
        counter++
        numberedTitle = shortTitle + counter
      }
      shortTitle = numberedTitle
    }
    usedTitlesCache.add(shortTitle)
    return shortTitle
  }

  // Try progressively longer combinations
  const strategies = [
    () => extractMeaningfulChars(title, 2), // 2 chars
    () => extractMeaningfulChars(title, 3), // 3 chars
    () => extractMeaningfulChars(title, 4) // 3 chars + first letter of second word
  ]

  let foundUnique = false
  for (const strategy of strategies) {
    shortTitle = strategy()
    if (!usedTitlesCache.has(shortTitle)) {
      usedTitlesCache.add(shortTitle)
      foundUnique = true
      break
    }
  }

  // Only add numbers as the very last resort
  if (!foundUnique) {
    shortTitle = extractMeaningfulChars(title, 4)
    let counter = 1
    let numberedTitle = shortTitle + counter
    while (usedTitlesCache.has(numberedTitle)) {
      counter++
      numberedTitle = shortTitle + counter
    }
    shortTitle = numberedTitle
    usedTitlesCache.add(shortTitle)
  }

  return shortTitle
}

/**
 * Generates unique short titles for a collection of posts
 * @param {Array} posts - Array of post objects with title property
 * @returns {Array} - Array of post objects with shortTitle property added
 */
export function generateShortTitles (posts) {
  if (!Array.isArray(posts)) return []

  // Clear the cache to start fresh
  clearShortTitlesCache()

  const results = []

  for (const post of posts) {
    if (!post || !post.title) {
      results.push({ ...post, shortTitle: '' })
      continue
    }

    // If shortTitle already exists, add it to cache and keep it
    if (post.shortTitle) {
      usedTitlesCache.add(post.shortTitle)
      results.push(post)
      continue
    }

    // Generate new short title
    const shortTitle = generateShortTitle(post.title)
    results.push({ ...post, shortTitle })
  }

  return results
}

/**
 * Legacy function for backwards compatibility - generates short title for a single post
 * considering already used titles
 * @param {string} title - The title to shorten
 * @param {Array} usedTitles - Array of already used short titles
 * @returns {string} - Generated short title
 */
export function generateSingleShortTitle (title, usedTitles = []) {
  if (!title) return ''

  const usedSet = new Set(usedTitles)
  let length = 2 // Start with minimum 2 characters
  let shortTitle = ''

  while (length <= title.length) {
    shortTitle = extractMeaningfulChars(title, length)

    if (!usedSet.has(shortTitle)) {
      return shortTitle
    }
    length++
  }

  // If we couldn't find a unique title, append a number
  let counter = 1
  let numberedTitle = shortTitle + counter
  while (usedSet.has(numberedTitle)) {
    counter++
    numberedTitle = shortTitle + counter
  }

  return numberedTitle
}

export default generateShortTitles
